/**
 * ResumeSessionHero component
 * Implements US-015 T2; story: "Resume my open session instead of starting a new one"
 *
 * Renders a positive "Resume your session" hero when a student has an active session,
 * replacing the empty CTA slot that previously existed (BUG-20260222-181734).
 *
 * Enforces TO-LL-021: ONE resume action only — the SessionCard's "Continue" button
 * IS the resume affordance; this component deliberately does NOT add a second standalone
 * "Resume" button alongside it. The hero text + locked-slot message are the new content.
 *
 * Enforces TO-LL-013: navigate target is constructed from a validated internal session id
 * (/session/{id}) — only via the existing SessionCard, never from a server-supplied URL.
 *
 * Purely presentational: no data fetching, no React Query, no Zustand.
 * If activeSession is undefined/null the component renders nothing (defensive guard).
 */

import { SessionCard } from '@/components/SessionCard';
import type { Session } from '@/types/session';

export interface ResumeSessionHeroProps {
  /** The student's current active session. If undefined/null, component renders null. */
  activeSession: Session | undefined | null;
}

/**
 * ResumeSessionHero - Positive resume affordance for students with an active session.
 *
 * Layout:
 *   Card shell
 *     ├─ Hero heading: "You have an open session"
 *     ├─ Sub-text: "Finish this session before you can start a new one" (AC-2)
 *     └─ SessionCard (existing component — provides Continue button navigating to /session/{id})
 *
 * The locked-slot message carries role="status" aria-live="polite" so screen readers
 * announce it when it appears (accessibility).
 */
export function ResumeSessionHero({ activeSession }: ResumeSessionHeroProps) {
  // Defensive guard (TO-LL-021): render nothing rather than crash on missing data.
  if (!activeSession) {
    return null;
  }

  return (
    <div className="mb-2">
      <div className="relative overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 p-6 shadow-lg">
        <p className="mb-3 text-[11px] font-extrabold uppercase tracking-wider text-otter-orange-600">
          <span aria-hidden="true">▶</span> In progress
        </p>

        {/* Hero heading */}
        <h2 className="mb-1 font-display text-xl font-semibold text-otter-earth-800">
          You have an open session
        </h2>

        {/* Locked-slot message — exact AC-2 copy; aria-live so screen readers announce it */}
        <p className="mb-4 text-sm text-otter-earth-700" role="status" aria-live="polite">
          Finish this session before you can start a new one
        </p>

        {/* Resume affordance: reuse the existing SessionCard which already renders a
            "Continue" button navigating to /session/{sessionId}.
            Per TO-LL-021 we do NOT add a second standalone Resume button — SessionCard's
            Continue IS the single resume action. */}
        <SessionCard session={activeSession} />
      </div>
    </div>
  );
}
